<script lang="ts">
	import arrowRightIcon from "@assets/icons/arrow_right_alt.svg?raw";
	import Icon from "@components/common/Icon.svelte";
	import { type Component, tick } from "svelte";
	import type { EventHandler } from "svelte/elements";
	import { SvelteMap } from "svelte/reactivity";
	import type { Chromosome } from "@core/schema/Chromosome.js";
	import type { Product } from "@core/schema/Product.js";
	import { chromosomeLocationColorColorMap } from "@core/schema/chromosomeLocationColorColorMap.js";
	import { chromosomeLocations } from "@core/schema/chromosomeLocations.js";
	import { getProductPath } from "@core/schema/paths/getProductPath.js";
	import { getChromosomeLocationText } from "@core/schema/utils/getChromosomeLocationText.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const neutralColor = "#59B2F4";
	const chromosomeImports = import.meta.glob("./svgs/*.svelte");

	const { chromosome, products }: { chromosome: Chromosome; products: Product[] } = $props();
	const { tenant } = getGlobalTemplateProps();

	let ChromosomeComponent = $state<Component | null>(null);
	const chromosomeProducts = $derived(
		products.filter((product) => product.locations.some((location) => location.chromosome === chromosome)),
	);
	const locations = $derived(chromosomeLocations.filter((location) => location.chromosome === chromosome));
	let errors = $state<string[]>([]);

	let svgRect = $state<DOMRect | null>(null);
	let currentPaths = $state<SVGPathElement[]>([]);
	let currentInitialFills = $state<string[]>([]);
	const currentPathRects = new SvelteMap<SVGPathElement, DOMRect>();
	const productElements: Record<Product["id"], HTMLElement> = $state({});
	let productWrapper = $state<HTMLDivElement | null>(null);

	let hoveredPath = $state<SVGPathElement | undefined>(undefined);
	let hoveredProduct = $state<Product | undefined>(undefined);

	let yOffset = $state(0);
	let windowScrollY = $state(0);
	let chromosomeHeight = $state(0);
	let chromosomeComponentWrapper = $state<HTMLElement | null>(null);
	const offset = 100;

	$effect(() => {
		if (chromosomeComponentWrapper === null) {
			return;
		}

		void windowScrollY;
		const { y, height } = chromosomeComponentWrapper.getBoundingClientRect();
		yOffset = Math.min(y > offset ? 0 : Math.abs(y - offset), height - chromosomeHeight);
	});

	const loadedHovers = $state<[SVGPathElement, () => void][]>([]);
	async function load(): Promise<void> {
		const chromosomeSvg = (await chromosomeImports[`./svgs/${chromosome}.svelte`]?.()) as { default: Component };
		ChromosomeComponent = chromosomeSvg.default;

		await tick();

		const svg = document.querySelector<SVGElement>(`svg[id="${chromosome}"]`);
		if (!svg) {
			throw new Error(`Chromosome SVG not found: ${chromosome}`);
		}

		svg.addEventListener("mouseleave", (): void => {
			hoveredPath = undefined;
		});

		svgRect = svg.getBoundingClientRect();

		errors = [];
		currentPaths = [];
		currentPathRects.clear();
		for (const location of locations) {
			currentPaths.push(
				...document.querySelectorAll<SVGPathElement>(`path[data-id="${getChromosomeLocationText(location)}"]`),
			);
			if (currentPaths.length === 0) {
				errors.push(getChromosomeLocationText(location));
			} else {
				currentInitialFills = currentPaths
					.values()
					.map((path) => path.style.fill)
					.toArray();
				for (const path of currentPaths) {
					// eslint-disable-next-line no-loop-func, @typescript-eslint/no-loop-func
					function onmouseover(): void {
						hoveredPath = path;
					}

					currentPathRects.set(path, path.getBoundingClientRect());
					path.addEventListener("mouseover", onmouseover);
					loadedHovers.push([path, onmouseover]);
				}
			}
		}
	}

	function unload(): void {
		for (const [path, onmouseover] of loadedHovers) {
			path.removeEventListener("mouseover", onmouseover);
		}
	}

	$effect(() => {
		void load();
		return (): void => {
			unload();
		};
	});

	$effect(() => {
		for (const path of currentPaths) {
			const hoveredProductLocation = hoveredProduct?.locations.find(
				(location) => getChromosomeLocationText(location) === path.dataset.id,
			);
			path.style.fill =
				(path.dataset.id === hoveredPath?.dataset.id || hoveredProductLocation) && path.dataset.noFill === undefined
					? hoveredProductLocation
						? chromosomeLocationColorColorMap[hoveredProductLocation.color]
						: neutralColor
					: (currentInitialFills[currentPaths.indexOf(path)] ?? "initial");
		}
	});

	function selectProduct(product: Product): EventHandler {
		return () => {
			hoveredProduct = product;
		};
	}
	function unselectProduct(product: Product): EventHandler {
		return () => {
			if (hoveredProduct?.id === product.id) {
				hoveredProduct = undefined;
			}
		};
	}

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

<svelte:window bind:scrollY={windowScrollY} />

{#snippet pathHoverEffect(path: SVGPathElement, _color: string)}
	<div
		class={[
			"absolute right-full pointer-events-none leading-none -translate-y-1/2 mr-2 xl:mr-6 font-medium text-md/6 hidden md:block",
		]}
		style="top: {(currentPathRects.get(path)?.top ?? 0) -
			(svgRect?.top ?? 0) +
			path.getBoundingClientRect().height / 2 +
			yOffset}px; text-color: {neutralColor};"
	>
		{path.dataset.id}
	</div>
{/snippet}

{#snippet joinHoverEffect(product: Product, path: SVGPathElement)}
	{@const productRect = productElements[product.id]?.getBoundingClientRect()}
	{@const pathRect = currentPathRects.get(path)}
	{#if productRect && pathRect}
		{@const productRectTop =
			productRect.top - (productWrapper?.getBoundingClientRect().top ?? 0) + productRect.height / 2}
		{@const pathRectTop = pathRect.top - (svgRect?.top ?? 0) + pathRect.height / 2 + yOffset}
		{@const productAbovePath = productRectTop < pathRectTop}
		{@const top = productAbovePath ? productRectTop : pathRectTop}
		{@const height = productAbovePath ? pathRectTop - productRectTop : productRectTop - pathRectTop}
		{@const overlapPxForRoundingErrors = 2}
		{@const borderWidth = 2}
		<div
			class={["absolute"]}
			style="border-bottom-width: {borderWidth}px; top: {top + height / 2 - borderWidth / 2}px; height: {height / 2 +
				overlapPxForRoundingErrors +
				borderWidth / 2}px; {productAbovePath
				? `left: 0; right: 50%; border-right-width: ${borderWidth}px; `
				: `left: calc(50% - ${borderWidth}px); right: 0; border-left-width: ${borderWidth}px; `}; border-color: {neutralColor};"
		></div>

		<div
			class={["absolute"]}
			style="border-top-width: {borderWidth}px; top: {top}px; height: {height / 2 +
				overlapPxForRoundingErrors +
				borderWidth / 2}px; {productAbovePath
				? `left: calc(50% - ${borderWidth}px); right: 0; border-left-width: ${borderWidth}px; `
				: `left: 0; right: calc(50%); border-right-width: ${borderWidth}px; `}; border-color: {neutralColor};"
		></div>
	{/if}
{/snippet}

<div class="grid grid-cols-1 md:grid-cols-[auto,6.625rem,1fr]">
	<div class="relative ml-14" bind:this={chromosomeComponentWrapper}>
		<div class="hidden md:block" bind:clientHeight={chromosomeHeight} style="transform: translateY({yOffset}px)">
			<ChromosomeComponent />
		</div>

		{#if hoveredPath !== undefined}
			{@render pathHoverEffect(hoveredPath, neutralColor)}
		{/if}
		{#if hoveredProduct !== undefined}
			{#each hoveredProduct.locations as location (location)}
				{@const paths = currentPaths.filter((path) => path.dataset.id === getChromosomeLocationText(location))}
				{#each paths as path, index (`${path.dataset.id}/${index}`)}
					{@render pathHoverEffect(path, chromosomeLocationColorColorMap[location.color])}
				{/each}
			{/each}
		{/if}
	</div>

	<div class="relative hidden md:block">
		{#if hoveredPath !== undefined}
			{@const connectedProducts = chromosomeProducts.filter((product) =>
				product.locations.some((location) => getChromosomeLocationText(location) === hoveredPath?.dataset.id),
			)}
			{#each connectedProducts as product (product.id)}
				{@render joinHoverEffect(product, hoveredPath)}
			{/each}
		{/if}
		{#if hoveredProduct !== undefined}
			{#each hoveredProduct.locations as location (location)}
				{@const paths = currentPaths.filter((path) => path.dataset.id === getChromosomeLocationText(location))}
				{#each paths as path, index (`${path.dataset.id}/${index}`)}
					{@render joinHoverEffect(hoveredProduct, path)}
				{/each}
			{/each}
		{/if}
	</div>

	<div class="flex flex-col gap-2" bind:this={productWrapper}>
		{#each chromosomeProducts as product (product.id)}
			{@const active =
				hoveredProduct?.id === product.id ||
				product.locations.some((location) =>
					hoveredPath ? getChromosomeLocationText(location) === hoveredPath.dataset.id : false,
				)}
			<a
				bind:this={productElements[product.id]}
				href={getProductPath(tenant, product)}
				class={[
					"py-3.5 px-6 text-blue font-bold cursor-pointer rounded-lg bg-white border-2 flex justify-between gap-4",
					active ? "border-2" : "",
				]}
				style="border-color: {active ? neutralColor : 'transparent'}"
				onfocus={selectProduct(product)}
				onfocusout={unselectProduct(product)}
				onmouseleave={unselectProduct(product)}
				onmouseover={selectProduct(product)}
			>
				<span>{product.name[tenant.locale]}</span>
				<div
					class={[
						"aspect-square h-6 bg-blue rounded-full self-start flex items-center justify-center",
						active ? "" : "hidden",
					]}
				>
					<Icon icon={arrowRightIcon} class="text-green w-3.5" />
				</div>
			</a>
		{/each}
	</div>
</div>
